import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Image as ImageIcon,
  Paperclip,
  Plus,
  Save,
  Trash2,
  Upload,
  Wand2,
  X,
} from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Select,
  cn,
  useToast,
} from '@solara/ui-shared';
import { computeInvoiceTotals, InvoiceInputSchema } from '@solara/domain';
import {
  generateInvoiceFormSample,
  generateSampleLineItems,
  generateUaeTrn,
} from '@solara/sample-data';
import { usePeppolConnectivity } from '@solara/peppol-tickstar/react';
import { useCreateInvoice } from '@solara/api-client/react';
import type { CreateInvoiceInput } from '@solara/api-client';

type VatRate = 5 | 0 | 'EXEMPT';

interface LineRow {
  id: string;
  description: string;
  quantity: string;
  unitPrice: string;
  vatRate: VatRate;
}

interface InvoiceForm {
  buyerTrn: string;
  buyerName: string;
  invoiceNumber: string;
  issueDate: string;
  deliveryDate: string;
  currency: 'AED';
  lineItems: LineRow[];
}

const SELLER_TRN = '100000000099999';
const SELLER_TENANT_ID = 'a1b2c3d4-2222-2222-2222-222222222222';

const DRAFT_KEY = 'solara.portal.invoiceDraft.v1';
const DRAFT_DEBOUNCE_MS = 800;

// Spec § Modul 2: ≤ 5 attachments, ≤ 10 MB each, PDF / JPG / PNG only.
const ATTACHMENT_MAX_COUNT = 5;
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
const ATTACHMENT_MIMES = new Set(['application/pdf', 'image/jpeg', 'image/png']);

interface AttachmentEntry {
  /** Stable id for keying / removal. */
  id: string;
  name: string;
  sizeBytes: number;
  mime: string;
}

function humanSize(bytes: number, locale: string): string {
  const fmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  if (bytes >= 1024 * 1024) return `${fmt.format(bytes / 1024 / 1024)} MB`;
  if (bytes >= 1024) return `${fmt.format(bytes / 1024)} KB`;
  return `${bytes} B`;
}

interface DraftEnvelope {
  savedAt: string;
  form: InvoiceForm;
  uploadedFile: { name: string; rows: number } | null;
}

function makeRow(seed = ''): LineRow {
  return {
    id: `${Date.now()}-${seed || Math.random().toString(36).slice(2, 8)}`,
    description: '',
    quantity: '1',
    unitPrice: '0',
    vatRate: 5,
  };
}

const initialForm: InvoiceForm = {
  buyerTrn: '',
  buyerName: '',
  invoiceNumber: '',
  issueDate: new Date().toISOString().slice(0, 10),
  deliveryDate: '',
  currency: 'AED',
  lineItems: [makeRow('init')],
};

function readDraft(): DraftEnvelope | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DraftEnvelope;
  } catch {
    return null;
  }
}

function writeDraft(envelope: DraftEnvelope): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(envelope));
  } catch {
    /* quota — ignore */
  }
}

function clearDraft(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(DRAFT_KEY);
}

export default function NewInvoicePage() {
  const { t } = useTranslation('invoice');
  const { t: tErr } = useTranslation('errors');
  const { t: tP1 } = useTranslation('p1');
  const { t: tFormErr } = useTranslation('invoice');
  const { t: tTs } = useTranslation('tickstar');
  const { locale, direction } = useI18n();
  const { toast } = useToast();
  const aedFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED' });
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' });

  const peppolConn = usePeppolConnectivity();
  const createInvoice = useCreateInvoice();
  const [form, setForm] = useState<InvoiceForm>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);
  const [peppolStatus, setPeppolStatus] = useState<
    | { phase: 'idle' }
    | { phase: 'sending' }
    | { phase: 'sent'; invoiceId: string; messageIdentifier?: string | null }
    | { phase: 'failed'; error: string }
  >({ phase: 'idle' });
  const [uploadedFile, setUploadedFile] = useState<{ name: string; rows: number } | null>(null);
  const [attachments, setAttachments] = useState<AttachmentEntry[]>([]);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [draftPrompt, setDraftPrompt] = useState<DraftEnvelope | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [attDragOver, setAttDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const attachmentInput = useRef<HTMLInputElement | null>(null);
  // M3 spec — UAE compliance markers + multi-currency state.
  const [complianceMarks, setComplianceMarks] = useState<{
    isFreeZoneTransaction: boolean;
    isExport: boolean;
    customsDeclarationId: string;
    counterpartyLocationType: '' | 'MAINLAND' | 'FREE_ZONE' | 'DESIGNATED_ZONE';
    paymentTerms: string;
    paymentReference: string;
    dueDate: string;
    invoiceType: 'STANDARD' | 'CREDIT_NOTE' | 'DEBIT_NOTE' | 'PROFORMA' | 'SELF_BILLED';
    currencyOverride: string; // 'AED' or 'USD'/'EUR'/'GBP'/'SAR'
  }>({
    isFreeZoneTransaction: false,
    isExport: false,
    customsDeclarationId: '',
    counterpartyLocationType: '',
    paymentTerms: '',
    paymentReference: '',
    dueDate: '',
    invoiceType: 'STANDARD',
    currencyOverride: 'AED',
  });

  // On mount: detect any pre-existing draft and offer to resume.
  useEffect(() => {
    const existing = readDraft();
    if (existing) setDraftPrompt(existing);
  }, []);

  // Debounced auto-save.
  useEffect(() => {
    if (done) return;
    // Skip auto-save if the user has not actually edited anything.
    if (form === initialForm && uploadedFile == null) return;
    const handle = window.setTimeout(() => {
      const env: DraftEnvelope = {
        savedAt: new Date().toISOString(),
        form,
        uploadedFile,
      };
      writeDraft(env);
      setSavedAt(new Date(env.savedAt));
    }, DRAFT_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [form, uploadedFile, done]);

  const totals = useMemo(() => {
    const items = form.lineItems
      .map((r) => {
        const q = parseFloat(r.quantity);
        const p = parseFloat(r.unitPrice);
        if (!isFinite(q) || !isFinite(p)) return null;
        return { description: r.description, quantity: q, unitPrice: p, vatRate: r.vatRate };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
    return computeInvoiceTotals(items);
  }, [form.lineItems]);

  function updateField<K extends keyof InvoiceForm>(key: K, val: InvoiceForm[K]) {
    setForm((s) => ({ ...s, [key]: val }));
    setErrors((e) => {
      const next = { ...e };
      delete next[key];
      return next;
    });
  }

  function updateRow(id: string, patch: Partial<LineRow>) {
    setForm((s) => ({
      ...s,
      lineItems: s.lineItems.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));
  }

  function addRow() {
    setForm((s) => ({ ...s, lineItems: [...s.lineItems, makeRow()] }));
  }

  function removeRow(id: string) {
    setForm((s) => ({
      ...s,
      lineItems: s.lineItems.length > 1 ? s.lineItems.filter((r) => r.id !== id) : s.lineItems,
    }));
  }

  function addAttachments(files: FileList | File[]) {
    const incoming = Array.from(files);
    let added = 0;
    setAttachments((current) => {
      const next = [...current];
      for (const f of incoming) {
        if (next.length >= ATTACHMENT_MAX_COUNT) {
          toast({ title: t('attachments.tooMany'), tone: 'warning' });
          break;
        }
        if (!ATTACHMENT_MIMES.has(f.type)) {
          toast({ title: t('attachments.wrongType', { name: f.name }), tone: 'warning' });
          continue;
        }
        if (f.size > ATTACHMENT_MAX_BYTES) {
          toast({ title: t('attachments.tooLarge', { name: f.name }), tone: 'warning' });
          continue;
        }
        next.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}-${added}`,
          name: f.name,
          sizeBytes: f.size,
          mime: f.type,
        });
        added++;
      }
      return next;
    });
    if (added > 0) {
      const last = incoming.find((f) => ATTACHMENT_MIMES.has(f.type) && f.size <= ATTACHMENT_MAX_BYTES);
      if (last) {
        toast({
          title: t('attachments.added', { name: last.name }),
          description: t('attachments.addedBody'),
          tone: 'success',
        });
      }
    }
  }

  function removeAttachment(id: string) {
    setAttachments((s) => s.filter((a) => a.id !== id));
  }

  function fillWithSample() {
    const sample = generateInvoiceFormSample();
    setForm({
      buyerTrn: sample.buyerTrn,
      buyerName: sample.buyerName,
      invoiceNumber: sample.invoiceNumber,
      issueDate: sample.issueDate,
      deliveryDate: '',
      currency: 'AED',
      lineItems: sample.lineItems.map((it, idx) => ({
        id: `sample-${Date.now()}-${idx}`,
        description: it.description,
        quantity: String(it.quantity),
        unitPrice: it.unitPrice.toFixed(2),
        vatRate: it.vatRate,
      })),
    });
    setErrors({});
  }

  function handleFile(file: File) {
    // We don't actually parse here — the prefill is a deterministic sample
    // that resembles what a real CSV/XML parser would extract.
    const items = generateSampleLineItems('trading').map((i, idx) => ({
      id: `csv-${Date.now()}-${idx}`,
      description: i.description,
      quantity: String(i.quantity),
      unitPrice: i.unitPrice.toFixed(2),
      vatRate: i.vatRate,
    }));
    const buyerTrn = generateUaeTrn();
    setForm((s) => ({
      ...s,
      buyerTrn,
      buyerName: 'Demo Buyer LLC',
      invoiceNumber: `IMP-${new Date().getFullYear()}-${Math.floor(Math.random() * 9999)
        .toString()
        .padStart(4, '0')}`,
      lineItems: items,
    }));
    setUploadedFile({ name: file.name, rows: items.length });
    toast({
      title: t('upload.loadedToast', { name: file.name }),
      description: t('upload.loadedToastBody', { rows: items.length }),
      tone: 'success',
    });
  }

  function clearUpload() {
    setUploadedFile(null);
    toast({ title: t('upload.removed'), tone: 'info' });
  }

  function saveDraft() {
    const env: DraftEnvelope = {
      savedAt: new Date().toISOString(),
      form,
      uploadedFile,
    };
    writeDraft(env);
    setSavedAt(new Date(env.savedAt));
    toast({ title: t('drafts.saved'), tone: 'success' });
  }

  function discardDraft() {
    clearDraft();
    setDraftPrompt(null);
    setSavedAt(null);
    toast({ title: t('drafts.discarded'), tone: 'info' });
  }

  function resumeDraft() {
    if (!draftPrompt) return;
    setForm(draftPrompt.form);
    setUploadedFile(draftPrompt.uploadedFile);
    setSavedAt(new Date(draftPrompt.savedAt));
    setDraftPrompt(null);
    toast({
      title: t('drafts.loaded'),
      description: t('drafts.loadedBody', { when: dateFmt.format(new Date(draftPrompt.savedAt)) }),
      tone: 'info',
    });
  }

  function submit() {
    const candidate = {
      tenantId: SELLER_TENANT_ID,
      invoiceNumber: form.invoiceNumber,
      sellerTrn: SELLER_TRN,
      buyerTrn: form.buyerTrn,
      buyerName: form.buyerName,
      issueDate: form.issueDate,
      deliveryDate: form.deliveryDate ? form.deliveryDate : undefined,
      currency: 'AED' as const,
      lineItems: form.lineItems
        .map((r) => ({
          description: r.description,
          quantity: parseFloat(r.quantity),
          unitPrice: parseFloat(r.unitPrice),
          vatRate: r.vatRate,
        }))
        .filter((it) => isFinite(it.quantity) && isFinite(it.unitPrice)),
    };
    const parsed = InvoiceInputSchema.safeParse(candidate);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path.join('.');
        if (!next[path]) next[path] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});

    // Hand the invoice off to the Solara Core API; the backend handles the
    // PINT-AE build, Schematron validation, signing, and Peppol dispatch via
    // TickStar internally — no need for the front-end to call the provider.
    const input: CreateInvoiceInput = {
      invoiceNumber: parsed.data.invoiceNumber,
      buyerTrn: parsed.data.buyerTrn,
      buyerName: parsed.data.buyerName,
      sellerTrn: parsed.data.sellerTrn,
      issueDate: parsed.data.issueDate,
      deliveryDate: parsed.data.deliveryDate,
      currency: complianceMarks.currencyOverride || parsed.data.currency,
      // M3 UAE compliance markers — see the development plan §3.
      invoiceType: complianceMarks.invoiceType,
      isFreeZoneTransaction: complianceMarks.isFreeZoneTransaction,
      isExport: complianceMarks.isExport,
      customsDeclarationId: complianceMarks.customsDeclarationId || undefined,
      counterpartyLocationType: complianceMarks.counterpartyLocationType || undefined,
      paymentTerms: complianceMarks.paymentTerms || undefined,
      paymentReference: complianceMarks.paymentReference || undefined,
      dueDate: complianceMarks.dueDate || undefined,
      lineItems: parsed.data.lineItems.map((li) => ({
        description: li.description,
        quantity: li.quantity,
        unitPriceMinor: Math.round(li.unitPrice * 100),
        vatRate: typeof li.vatRate === 'number' ? li.vatRate : 0,
        vatCategory: li.vatRate === 'EXEMPT' ? 'E' : undefined,
      })),
    };
    setPeppolStatus({ phase: 'sending' });
    // Only flip into the "submitted" view after the backend confirms — a
    // failed POST should leave the user on the form so they can fix and retry.
    createInvoice
      .mutateAsync(input)
      .then((res) => {
        setPeppolStatus({
          phase: 'sent',
          invoiceId: res.id,
          messageIdentifier: res.messageId,
        });
        clearDraft();
        setSavedAt(null);
        setDone(true);
      })
      .catch((e) => {
        setPeppolStatus({ phase: 'failed', error: e instanceof Error ? e.message : String(e) });
      });
  }

  if (done) {
    return (
      <div className="max-w-xl mx-auto text-center py-12">
        <div className="mx-auto h-16 w-16 rounded-full bg-success/15 grid place-items-center text-success">
          <CheckCircle2 className="h-9 w-9" />
        </div>
        <h2 className="mt-6 text-2xl font-semibold tracking-tight">{t('new.submitted')}</h2>
        <p className="mt-2 text-fg-muted">{t('new.submittedHint')}</p>

        <div className="mt-6 rounded-lg border border-border bg-surface p-4 text-start text-sm">
          <div className="text-xs font-semibold text-fg-muted uppercase tracking-wide">
            Peppol delivery {peppolConn.mode === 'mock' ? '(mock)' : ''}
          </div>
          {peppolStatus.phase === 'idle' ? (
            <p className="mt-1 text-fg-muted">Preparing SBDH envelope…</p>
          ) : peppolStatus.phase === 'sending' ? (
            <p className="mt-1 text-fg-muted">Submitting to TickStar Transaction API…</p>
          ) : peppolStatus.phase === 'sent' ? (
            <div className="mt-1 space-y-1 font-mono text-xs">
              <div>
                <span className="text-fg-muted">invoiceId:</span>{' '}
                <Link to={`/invoices/${peppolStatus.invoiceId}`} className="text-brand-700 underline">
                  {peppolStatus.invoiceId}
                </Link>
              </div>
              {peppolStatus.messageIdentifier ? (
                <div>
                  <span className="text-fg-muted">messageId:</span>{' '}
                  {peppolStatus.messageIdentifier}
                </div>
              ) : null}
              <div className="text-fg-muted">
                Backend pipeline: VALIDATING → SIGNED → SENDING → DELIVERED.
              </div>
            </div>
          ) : (
            <p className="mt-1 text-danger">Peppol send failed: {peppolStatus.error}</p>
          )}
        </div>

        <div className="mt-6 flex justify-center gap-3">
          <Button
            onClick={() => {
              setDone(false);
              setForm(initialForm);
              setUploadedFile(null);
              setPeppolStatus({ phase: 'idle' });
            }}
            variant="outline"
          >
            {t('new.newInvoiceCta')}
          </Button>
          <Button asChild>
            <Link to="/invoices">{t('new.viewInvoices')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const errLabel = (path: string) => {
    const code = errors[path];
    if (!code) return undefined;
    return tErr(code, { defaultValue: code });
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" dir={direction}>
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('new.title')}</h1>
          <p className="text-fg-muted text-sm max-w-xl">{t('new.subtitle')}</p>
          {savedAt ? (
            <p className="text-xs text-fg-subtle mt-1 flex items-center gap-1">
              <Save className="h-3 w-3" />
              {t('drafts.saved')} · {dateFmt.format(savedAt)}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="ghost" size="sm" onClick={saveDraft} className="gap-2" title={t('drafts.saveLocalHint')}>
            <Save className="h-4 w-4" />
            {t('drafts.saveLocal')}
          </Button>
          <Button
            variant="outline"
            onClick={fillWithSample}
            className="gap-2"
            title={t('new.fillSampleHint')}
          >
            <Wand2 className="h-4 w-4" />
            {t('new.fillSample')}
          </Button>
        </div>
      </header>

      {peppolStatus.phase === 'failed' ? (
        <Card className="rounded-2xl border-danger/40 bg-danger/5">
          <CardBody className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-danger shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-medium text-danger">{tP1('invoiceNew.notSubmitted')}</div>
              <div className="text-fg-muted">{peppolStatus.error}</div>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {draftPrompt ? (
        <Card className="rounded-2xl border-brand-200 bg-brand-50">
          <CardBody className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Save className="h-5 w-5 text-brand-700 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-brand-900">{t('drafts.promptTitle')}</div>
                <div className="text-sm text-brand-800">
                  {t('drafts.promptBody', { when: dateFmt.format(new Date(draftPrompt.savedAt)) })}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={discardDraft}>
                {t('drafts.discard')}
              </Button>
              <Button size="sm" onClick={resumeDraft}>
                {t('drafts.resume')}
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : null}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('sections.upload')}</CardTitle>
          <p className="text-xs text-fg-muted">{t('sections.uploadHint')}</p>
        </CardHeader>
        <CardBody>
          <input
            ref={fileInput}
            type="file"
            className="hidden"
            accept=".csv,.xml,text/csv,application/xml,text/xml"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.currentTarget.value = '';
            }}
          />
          {uploadedFile ? (
            <div className="rounded-xl border border-success/30 bg-success/5 p-4 flex items-center gap-3">
              <FileText className="h-5 w-5 text-success shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{uploadedFile.name}</div>
                <div className="text-xs text-fg-muted">
                  {t('upload.loaded', { name: uploadedFile.name })}
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={clearUpload} aria-label={t('upload.removed')}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              onDragEnter={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const f = e.dataTransfer.files?.[0];
                if (f) handleFile(f);
              }}
              className={cn(
                'w-full rounded-xl border-2 border-dashed p-6 text-center transition-colors',
                dragOver
                  ? 'border-brand-500 bg-brand-50/40'
                  : 'border-border-strong bg-surface-muted/30 hover:bg-surface-muted/60',
              )}
            >
              <Upload className="h-7 w-7 mx-auto text-brand-500" />
              <p className="mt-2 text-sm font-medium">{t('upload.drop')}</p>
              <p className="text-xs text-fg-subtle mt-1">CSV · XML · ≤ 10 MB</p>
            </button>
          )}
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('sections.buyer')}</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-4 md:grid-cols-2">
          <Input
            label={t('fields.buyerTrn')}
            value={form.buyerTrn}
            onChange={(e) => updateField('buyerTrn', e.target.value.replace(/\s/g, ''))}
            placeholder="100000000005678"
            error={errLabel('buyerTrn')}
            inputMode="numeric"
            maxLength={15}
            className="num"
            dir="ltr"
            required
          />
          <Input
            label={t('fields.buyerName')}
            value={form.buyerName}
            onChange={(e) => updateField('buyerName', e.target.value)}
            placeholder="Emaar Properties PJSC"
            error={errLabel('buyerName')}
            required
          />
          <Input
            label={t('fields.invoiceNumber')}
            value={form.invoiceNumber}
            onChange={(e) => updateField('invoiceNumber', e.target.value)}
            placeholder="INV-2026-0001"
            error={errLabel('invoiceNumber')}
            required
          />
          <Input
            label={t('fields.issueDate')}
            type="date"
            value={form.issueDate}
            onChange={(e) => updateField('issueDate', e.target.value)}
            error={errLabel('issueDate')}
            dir="ltr"
            required
          />
          <Input
            label={t('fields.deliveryDate')}
            type="date"
            value={form.deliveryDate}
            onChange={(e) => updateField('deliveryDate', e.target.value)}
            error={errLabel('deliveryDate')}
            dir="ltr"
          />
          <Select
            label={t('fields.currency')}
            value={form.currency}
            onChange={(e) => updateField('currency', e.target.value as 'AED')}
            options={[{ value: 'AED', label: 'AED — UAE Dirham' }]}
          />
        </CardBody>
      </Card>

      {/* M3 UAE compliance + multi-currency block. See development plan §3. */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>UAE compliance markers</CardTitle>
          <p className="text-xs text-fg-muted">
            Free zone marker, reverse-charge handling, export with customs declaration ID,
            and currency conversion to AED.
          </p>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label={tP1('invoiceNew.invoiceTypeLabel')}
            value={complianceMarks.invoiceType}
            onChange={(e) =>
              setComplianceMarks((m) => ({
                ...m,
                invoiceType: e.target.value as typeof complianceMarks.invoiceType,
              }))
            }
            options={(['STANDARD', 'CREDIT_NOTE', 'DEBIT_NOTE', 'PROFORMA', 'SELF_BILLED'] as const).map(
              (v) => ({ value: v, label: tP1(`invoiceNew.invoiceTypes.${v}`) }),
            )}
          />
          <Select
            label={tP1('invoiceNew.settlementCurrency')}
            value={complianceMarks.currencyOverride}
            onChange={(e) =>
              setComplianceMarks((m) => ({ ...m, currencyOverride: e.target.value }))
            }
            options={(['AED', 'USD', 'EUR', 'GBP', 'SAR'] as const).map((v) => ({
              value: v,
              label: tP1(`invoiceNew.currencyLabels.${v}`),
            }))}
          />
          <Select
            label={tP1('invoiceNew.counterpartyLocation')}
            value={complianceMarks.counterpartyLocationType}
            onChange={(e) =>
              setComplianceMarks((m) => ({
                ...m,
                counterpartyLocationType: e.target.value as typeof complianceMarks.counterpartyLocationType,
                isFreeZoneTransaction: e.target.value === 'FREE_ZONE',
              }))
            }
            options={[
              { value: '', label: '—' },
              { value: 'MAINLAND', label: tP1('invoiceNew.locationTypes.MAINLAND') },
              { value: 'FREE_ZONE', label: tP1('invoiceNew.locationTypes.FREE_ZONE') },
              { value: 'DESIGNATED_ZONE', label: tP1('invoiceNew.locationTypes.DESIGNATED_ZONE') },
            ]}
          />
          <Input
            label={tP1('invoiceNew.dueDate')}
            type="date"
            value={complianceMarks.dueDate}
            onChange={(e) => setComplianceMarks((m) => ({ ...m, dueDate: e.target.value }))}
          />
          <Input
            label={tP1('invoiceNew.paymentTerms')}
            value={complianceMarks.paymentTerms}
            onChange={(e) => setComplianceMarks((m) => ({ ...m, paymentTerms: e.target.value }))}
            placeholder={tP1('invoiceNew.paymentTermsPlaceholder')}
          />
          <Input
            label={tP1('invoiceNew.paymentReference')}
            value={complianceMarks.paymentReference}
            onChange={(e) => setComplianceMarks((m) => ({ ...m, paymentReference: e.target.value }))}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={complianceMarks.isFreeZoneTransaction}
              onChange={(e) =>
                setComplianceMarks((m) => ({ ...m, isFreeZoneTransaction: e.target.checked }))
              }
            />
            {tP1('invoiceNew.freeZoneTransaction')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={complianceMarks.isExport}
              onChange={(e) =>
                setComplianceMarks((m) => ({ ...m, isExport: e.target.checked }))
              }
            />
            {tP1('invoiceNew.exportTransaction')}
          </label>
          {complianceMarks.isExport && (
            <Input
              className="sm:col-span-2"
              label={tP1('invoiceNew.customsDeclarationId')}
              value={complianceMarks.customsDeclarationId}
              onChange={(e) =>
                setComplianceMarks((m) => ({ ...m, customsDeclarationId: e.target.value }))
              }
              placeholder={tP1('invoiceNew.customsDeclarationPlaceholder')}
              required
            />
          )}
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('attachments.title')}</CardTitle>
          <p className="text-xs text-fg-muted">{t('attachments.subtitle')}</p>
        </CardHeader>
        <CardBody className="space-y-3">
          <input
            ref={attachmentInput}
            type="file"
            multiple
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            onChange={(e) => {
              if (e.target.files) addAttachments(e.target.files);
              e.currentTarget.value = '';
            }}
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-fg-subtle">
              {t('attachments.limit', {
                count: attachments.length,
                max: ATTACHMENT_MAX_COUNT,
                used: humanSize(attachments.reduce((s, a) => s + a.sizeBytes, 0), locale),
                maxBytes: humanSize(ATTACHMENT_MAX_BYTES * ATTACHMENT_MAX_COUNT, locale),
              })}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => attachmentInput.current?.click()}
              disabled={attachments.length >= ATTACHMENT_MAX_COUNT}
              className="gap-2"
            >
              <Paperclip className="h-4 w-4" />
              {t('attachments.browse')}
            </Button>
          </div>

          <button
            type="button"
            onClick={() => attachmentInput.current?.click()}
            onDragEnter={(e) => {
              e.preventDefault();
              setAttDragOver(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setAttDragOver(true);
            }}
            onDragLeave={() => setAttDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setAttDragOver(false);
              if (e.dataTransfer.files) addAttachments(e.dataTransfer.files);
            }}
            disabled={attachments.length >= ATTACHMENT_MAX_COUNT}
            className={cn(
              'w-full rounded-xl border-2 border-dashed p-4 text-center transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              attDragOver
                ? 'border-brand-500 bg-brand-50/40'
                : 'border-border-strong bg-surface-muted/30 hover:bg-surface-muted/60',
            )}
          >
            <Paperclip className="h-5 w-5 mx-auto text-brand-500" />
            <p className="mt-1.5 text-sm font-medium">{t('attachments.drop')}</p>
            <p className="text-xs text-fg-subtle mt-0.5">PDF · JPG · PNG · ≤ 10 MB</p>
          </button>

          {attachments.length > 0 ? (
            <ul className="space-y-2">
              {attachments.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2"
                >
                  <span className="text-fg-subtle shrink-0">
                    {a.mime.startsWith('image/') ? (
                      <ImageIcon className="h-4 w-4" />
                    ) : (
                      <FileText className="h-4 w-4" />
                    )}
                  </span>
                  <span className="text-sm font-medium truncate flex-1" dir="ltr">
                    {a.name}
                  </span>
                  <span className="text-xs text-fg-subtle num shrink-0" dir="ltr">
                    {humanSize(a.sizeBytes, locale)}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeAttachment(a.id)}
                    aria-label={t('attachments.remove')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-fg-subtle italic">{t('attachments.none')}</p>
          )}
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle>{t('items.title')}</CardTitle>
              <span
                className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 text-[10px] font-medium"
                title={tTs('pillars.ai.body')}
              >
                {tTs('pillars.ai.title')}
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={addRow} className="gap-2">
              <Plus className="h-4 w-4" />
              {t('items.addItem')}
            </Button>
          </div>
        </CardHeader>
        <CardBody className="space-y-3">
          {form.lineItems.map((row, idx) => (
            <div
              key={row.id}
              className="grid gap-3 rounded-xl border border-border p-3 sm:grid-cols-[minmax(0,1fr)_90px_120px_120px_auto]"
            >
              <Input
                label={idx === 0 ? t('items.description') : undefined}
                value={row.description}
                onChange={(e) => updateRow(row.id, { description: e.target.value })}
                placeholder={t('items.descriptionPlaceholder')}
              />
              <Input
                label={idx === 0 ? t('items.quantity') : undefined}
                value={row.quantity}
                onChange={(e) => updateRow(row.id, { quantity: e.target.value })}
                inputMode="decimal"
                className="num text-end"
                dir="ltr"
              />
              <Input
                label={idx === 0 ? t('items.unitPrice') : undefined}
                value={row.unitPrice}
                onChange={(e) => updateRow(row.id, { unitPrice: e.target.value })}
                inputMode="decimal"
                className="num text-end"
                dir="ltr"
              />
              <Select
                label={idx === 0 ? t('items.vatRate') : undefined}
                value={String(row.vatRate)}
                onChange={(e) => {
                  const v = e.target.value;
                  const next: VatRate = v === 'EXEMPT' ? 'EXEMPT' : (Number(v) as VatRate);
                  updateRow(row.id, { vatRate: next });
                }}
                options={[
                  { value: '5', label: t('items.vat.5') },
                  { value: '0', label: t('items.vat.0') },
                  { value: 'EXEMPT', label: t('items.vat.EXEMPT') },
                ]}
              />
              <div className={cn('flex items-end justify-end', idx === 0 ? 'pb-1' : '')}>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeRow(row.id)}
                  disabled={form.lineItems.length === 1}
                  aria-label={t('items.removeItem')}
                  className="text-fg-subtle hover:text-danger"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          {Object.keys(errors).length > 0 ? (
            <div className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
              {tFormErr('errors.fixBelow')}
            </div>
          ) : null}
        </CardBody>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('sections.totals')}</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 sm:grid-cols-3">
          <Total label={t('totals.net')} value={aedFmt.format(totals.totalNetAmount)} />
          <Total label={t('totals.vat')} value={aedFmt.format(totals.totalVatAmount)} />
          <Total
            label={t('totals.gross')}
            value={aedFmt.format(totals.totalGrossAmount)}
            highlight
          />
        </CardBody>
        <div className="px-6 pb-6 flex flex-wrap gap-3 items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {totals.vatByRate.map((r) => (
              <Badge key={String(r.rate)} tone="neutral">
                {r.rate === 'EXEMPT' ? t('items.vat.EXEMPT') : `${r.rate}%`} → {aedFmt.format(r.vat)}
              </Badge>
            ))}
          </div>
          <Button
            onClick={submit}
            size="lg"
            className="gap-2"
            disabled={peppolStatus.phase === 'sending'}
          >
            {peppolStatus.phase === 'sending' ? 'Submitting…' : t('new.submit')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Total({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border p-4',
        highlight ? 'border-brand-200 bg-brand-50' : 'border-border bg-surface',
      )}
    >
      <div className="text-xs uppercase tracking-wide text-fg-subtle">{label}</div>
      <div
        className={cn(
          'text-2xl font-semibold num mt-1',
          highlight ? 'text-brand-800' : 'text-fg',
        )}
      >
        {value}
      </div>
    </div>
  );
}

// --- TickStar payload helper ---------------------------------------

/**
 * Build a Peppol-style SBDH envelope around the customer-supplied invoice.
 *
 * This is intentionally minimal — the production Solara Core would build the
 * full UBL 2.1 PINT-AE document, run Schematron validation and sign it before
 * handing it to TickStar. For the demo we generate just enough XML for the
 * MockTickstarTransactionProvider to extract sender / receiver / messageId.
 */
function buildSbdhPayload(input: {
  invoiceNumber: string;
  sellerTrn: string;
  buyerTrn: string;
  issueDate: string;
}): string {
  const messageId = randomUuidV4();
  return `<?xml version="1.0" encoding="UTF-8"?>
<StandardBusinessDocument xmlns="http://www.unece.org/cefact/namespaces/StandardBusinessDocumentHeader">
  <StandardBusinessDocumentHeader>
    <HeaderVersion>1.0</HeaderVersion>
    <Sender>
      <Identifier Authority="iso6523-actorid-upis">0230:UAE-TRN-${input.sellerTrn}</Identifier>
    </Sender>
    <Receiver>
      <Identifier Authority="iso6523-actorid-upis">0230:UAE-TRN-${input.buyerTrn}</Identifier>
    </Receiver>
    <DocumentIdentification>
      <Standard>urn:oasis:names:specification:ubl:schema:xsd:Invoice-2</Standard>
      <TypeVersion>2.1</TypeVersion>
      <InstanceIdentifier>${messageId}</InstanceIdentifier>
      <Type>Invoice</Type>
      <CreationDateAndTime>${new Date().toISOString()}</CreationDateAndTime>
    </DocumentIdentification>
  </StandardBusinessDocumentHeader>
  <Invoice xmlns="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2"
           xmlns:cbc="urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2">
    <cbc:ID>${escapeXml(input.invoiceNumber)}</cbc:ID>
    <cbc:IssueDate>${input.issueDate}</cbc:IssueDate>
    <cbc:DocumentCurrencyCode>AED</cbc:DocumentCurrencyCode>
    <cbc:Note>Demo invoice submitted via the Solara customer portal (mock TickStar provider).</cbc:Note>
  </Invoice>
</StandardBusinessDocument>`;
}

function escapeXml(value: string): string {
  return value.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case "'": return '&apos;';
      default:  return '&quot;';
    }
  });
}

function randomUuidV4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
